import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { doc, onSnapshot } from 'firebase/firestore'
import { firestore } from '@/firebase'
import { useCustomerStore } from './customer'
import {
  USER_CHOICES_CURRENT_DOCUMENT_ID,
  userChoicesSchema,
  type UserChoices,
} from '@gh/common/models'
import createLogger from '@/utils/logger'

const logger = createLogger('stores/userChoices.ts')

export const useUserChoicesStore = defineStore('userChoices', () => {
  const customerStore = useCustomerStore()
  const userChoices = ref<UserChoices | null>(null)
  const isLoading = ref(false)
  let unsubscribeSnapshot: (() => void) | null = null

  const customerId = computed(() => customerStore.customer?.id ?? null)

  const planSelectedOnSignup = computed(() => userChoices.value?.planSelectedOnSignup ?? null)

  function signOut() {
    if (unsubscribeSnapshot) {
      unsubscribeSnapshot()
      unsubscribeSnapshot = null
    }
    userChoices.value = null
    isLoading.value = false
  }

  watch(
    customerId,
    () => {
      signOut()
      if (!customerId.value) {
        return
      }
      isLoading.value = true
      unsubscribeSnapshot = onSnapshot(
        doc(
          firestore,
          'customers',
          customerId.value,
          'user_choices',
          USER_CHOICES_CURRENT_DOCUMENT_ID,
        ),
        (docSnap) => {
          if (!docSnap.exists()) {
            userChoices.value = null
            isLoading.value = false
            return
          }
          const parsed = userChoicesSchema.safeParse({
            id: docSnap.id,
            ...docSnap.data(),
          })
          if (!parsed.success) {
            logger.error('user_choices parse failed', parsed.error)
            userChoices.value = null
            isLoading.value = false
            return
          }
          userChoices.value = parsed.data
          isLoading.value = false
        },
        (err) => {
          logger.error('user_choices subscription error', err)
          userChoices.value = null
          isLoading.value = false
        },
      )
    },
    { immediate: true },
  )

  return {
    userChoices,
    planSelectedOnSignup,
    isLoading,
    signOut,
  }
})
